import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, MessageCircle, Mail, MapPin, Phone, Languages } from "lucide-react";
import { WHATSAPP_URL, CONTACT_EMAIL, PHONE_DISPLAY, PHONE_TEL } from "../data/content";
import { useLang, useContent } from "../i18n";

const NAV = [
  { to: "/", key: "nav.accueil", id: "accueil" },
  { to: "/services", key: "nav.services", id: "services" },
  { to: "/creation-entreprise", key: "nav.creation", id: "creation-entreprise" },
  { to: "/diaspora", key: "nav.diaspora", id: "diaspora" },
  { to: "/etrangers", key: "nav.etrangers", id: "etrangers" },
  { to: "/a-propos", key: "nav.apropos", id: "a-propos" },
  { to: "/blog", key: "nav.blog", id: "blog" },
  { to: "/faq", key: "nav.faq", id: "faq" },
  { to: "/suivi", key: "nav.suivi", id: "suivi" },
];

const LangSwitcher = ({ mobile = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid={mobile ? "lang-switcher-mobile" : "lang-switcher"}
      className={`flex items-center rounded-full border border-white/20 p-0.5 ${mobile ? "w-fit" : ""}`}
      role="group"
      aria-label="Langue / Language"
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}${mobile ? "-mobile" : ""}`}
          className={`rounded-full px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider transition-colors duration-300 ${
            lang === l ? "bg-gold text-brand-deep" : "text-white/60 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

let logoCache;
const useSiteLogo = () => {
  const [logoUrl, setLogoUrl] = useState(logoCache || null);
  useEffect(() => {
    if (logoCache !== undefined) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/branding`)
      .then((r) => r.json())
      .then((d) => {
        logoCache = d.logo_url || null;
        setLogoUrl(d.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${d.logo_url}` : null);
      })
      .catch(() => { logoCache = null; });
  }, []);
  return logoUrl;
};

let concoursCache;
/** Détecte si un concours est publié (pour afficher le lien « Concours »). */
const useConcoursLive = () => {
  const [live, setLive] = useState(concoursCache || false);
  useEffect(() => {
    if (concoursCache !== undefined) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concours/current`)
      .then((r) => { concoursCache = r.ok; setLive(r.ok); })
      .catch(() => { concoursCache = false; });
  }, []);
  return live;
};

const TogoFlagEmblem = () => (
  <span className="relative block h-10 w-10 overflow-hidden rounded-xl shadow-lg ring-1 ring-black/10" role="img" aria-label="Drapeau du Togo">
    <span
      className="absolute inset-0"
      style={{ background: "linear-gradient(180deg, #006A4E 0 20%, #FFCE00 20% 40%, #006A4E 40% 60%, #FFCE00 60% 80%, #006A4E 80% 100%)" }}
    />
    <span className="absolute left-0 top-0 grid place-items-center bg-[#D21034]" style={{ width: "60%", height: "60%" }}>
      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white" aria-hidden="true">
        <path d="M12 1.8l2.6 7.2h7.6l-6.1 4.6 2.4 7.4L12 16.3l-6.5 4.7 2.4-7.4L1.8 9h7.6z" />
      </svg>
    </span>
  </span>
);

export const Logo = ({ light = false }) => {
  const logoUrl = useSiteLogo();
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
      {logoUrl ? (
        <img src={logoUrl} alt="DOC TOGO" data-testid="site-logo-image" className="h-10 w-10 rounded-xl bg-white/95 object-contain p-0.5 shadow-lg" />
      ) : (
        <TogoFlagEmblem />
      )}
      <span className={`font-display text-lg font-extrabold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>
        DOC<span className="text-brand">TOGO</span>
      </span>
    </Link>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();
  const concoursLive = useConcoursLive();
  useEffect(() => setOpen(false), [location.pathname]);
  const navItems = concoursLive
    ? [...NAV.slice(0, 7), { to: "/concours", key: "nav.concours", id: "concours" }, ...NAV.slice(7)]
    : NAV;

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-brand-deep/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 lg:h-20 max-w-[1500px] items-center justify-between gap-4 px-6">
        <div className="shrink-0 [&_span:last-child]:text-white"><Logo light /></div>
        <nav data-testid="desktop-nav" className="hidden [@media(min-width:1400px)]:flex items-center gap-5">
          {navItems.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-[13px] font-semibold transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-white/70 hover:text-white"
                }`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>
        <div className="hidden [@media(min-width:1400px)]:flex shrink-0 items-center gap-2.5">
          <LangSwitcher />
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white transition-[background-color,border-color] duration-300 hover:bg-white/10 hover:border-[#25D366]"
            aria-label="WhatsApp"
          >
            <MessageCircle className="h-4 w-4 text-[#25D366]" />
          </a>
          <Link
            to="/contact"
            data-testid="header-demarrer-btn"
            className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-gold px-4 py-2.5 text-[13px] font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5"
          >
            {t("cta.start")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="flex items-center gap-3 [@media(min-width:1400px)]:hidden">
          <LangSwitcher mobile />
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/20 text-white"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="[@media(min-width:1400px)]:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-white/10 bg-brand-deep px-6 py-6">
          <nav className="flex flex-col gap-1">
            {navItems.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.id}`}
                className={({ isActive }) =>
                  `rounded-xl px-3 py-3 text-base font-semibold transition-colors ${isActive ? "bg-white/5 text-gold" : "text-white/80 active:bg-white/5"}`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-demarrer-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-display font-bold text-brand-deep"
            >
              {t("cta.start")} <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 font-display font-bold text-white"
            >
              <MessageCircle className="h-4 w-4 text-[#25D366]" /> {t("cta.whatsappShort")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const { t } = useLang();
  const { SERVICES } = useContent();
  const concoursLive = useConcoursLive();
  const serviceLinks = [
    { id: "footer-link-documents", to: "/services", label: SERVICES[0].title },
    { id: "footer-link-entreprise", to: "/creation-entreprise", label: SERVICES[1].title },
    { id: "footer-link-diaspora", to: "/diaspora", label: SERVICES[2].title },
    { id: "footer-link-etrangers", to: "/etrangers", label: SERVICES[3].title },
    { id: "footer-link-entreprises", to: "/services", label: SERVICES[4].title },
  ];
  return (
    <footer data-testid="site-footer" className="bg-brand-deep text-white">
      <div className="flag-stripe h-1.5 w-full" />
      <div className="mx-auto max-w-7xl px-6 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo light />
          <p className="mt-5 text-sm leading-relaxed text-white/55">{t("footer.tagline")}</p>
        </div>
        <div>
          <h4 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold">{t("footer.services")}</h4>
          <ul className="mt-5 space-y-3 text-sm text-white/65">
            {serviceLinks.map((l) => (
              <li key={l.id}><Link data-testid={l.id} to={l.to} className="transition-colors hover:text-white">{l.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold">{t("footer.platform")}</h4>
          <ul className="mt-5 space-y-3 text-sm text-white/65">
            <li><Link data-testid="footer-link-apropos" to="/a-propos" className="transition-colors hover:text-white">{t("footer.apropos")}</Link></li>
            <li><Link data-testid="footer-link-faq" to="/faq" className="transition-colors hover:text-white">FAQ</Link></li>
            <li><Link data-testid="footer-link-blog" to="/blog" className="transition-colors hover:text-white">{t("footer.blog")}</Link></li>
            {concoursLive && (
              <li><Link data-testid="footer-link-concours" to="/concours" className="font-semibold text-gold transition-colors hover:text-gold-warm">{t("nav.concours")}</Link></li>
            )}
            <li><Link data-testid="footer-link-contact" to="/contact" className="transition-colors hover:text-white">{t("footer.demande")}</Link></li>
            <li><Link data-testid="footer-link-suivi" to="/suivi" className="transition-colors hover:text-white">{t("footer.suivi")}</Link></li>
            <li><Link data-testid="footer-link-ambassades" to="/ambassades-consulats" className="transition-colors hover:text-white">{t("footer.ambassades")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold">{t("footer.contact")}</h4>
          <ul className="mt-5 space-y-3 text-sm text-white/65">
            <li className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-gold shrink-0" /> Lomé, Togo</li>
            <li>
              <a data-testid="footer-email" href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-2.5 transition-colors hover:text-white">
                <Mail className="h-4 w-4 text-gold shrink-0" /> {CONTACT_EMAIL}
              </a>
            </li>
            <li>
              <a data-testid="footer-phone" href={PHONE_TEL} className="flex items-center gap-2.5 transition-colors hover:text-white">
                <Phone className="h-4 w-4 text-gold shrink-0" /> {PHONE_DISPLAY} <span className="text-white/40">{t("footer.callsWhats")}</span>
              </a>
            </li>
            <li className="flex items-center gap-2.5"><MessageCircle className="h-4 w-4 text-gold shrink-0" /> {t("footer.hours")}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col sm:flex-row items-center justify-between gap-3 px-6 py-6 text-xs text-white/40">
          <span className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <span>{t("footer.rights")}</span>
            <Link to="/politique-confidentialite" data-testid="footer-link-confidentialite" className="transition-colors hover:text-gold">{t("footer.confidentialite")}</Link>
            <Link to="/conditions-utilisation" data-testid="footer-link-conditions" className="transition-colors hover:text-gold">{t("footer.conditions")}</Link>
            <Link to="/admin" data-testid="footer-link-admin" className="text-white/25 transition-colors hover:text-gold">{t("footer.admin")}</Link>
          </span>
          <span className="font-mono uppercase tracking-[0.2em]">{t("footer.slogan")}</span>
        </div>
      </div>
    </footer>
  );
};

export const WhatsAppFloat = () => {
  const { t } = useLang();
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-btn"
      aria-label="WhatsApp"
      className="group fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-[max(1.25rem,env(safe-area-inset-right))] z-50 grid h-12 w-12 sm:h-14 sm:w-14 place-items-center rounded-full bg-[#25D366] shadow-xl shadow-emerald-900/30 transition-transform duration-300 hover:scale-110"
    >
      <MessageCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
      <span className="pointer-events-none absolute right-16 whitespace-nowrap rounded-full bg-brand-deep px-4 py-2 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-300 group-hover:opacity-100">
        {t("float.help")}
      </span>
    </a>
  );
};

export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};
